/**
 * UIContext - Manages UI state
 * 
 * Handles:
 * - Status messages
 * - Loading state
 * - Modal visibility
 * - UI settings (jump to waypoint, robot type)
 */
import { createContext, useContext, useState, useCallback } from 'react';

const UIContext = createContext(null);

export const useUI = () => {
  const context = useContext(UIContext);
  if (!context) {
    throw new Error('useUI must be used within UIProvider');
  }
  return context;
};

export const UIProvider = ({ children }) => {
  // Status state
  const [statusMessage, setStatusMessage] = useState('Ready');
  const [statusType, setStatusType] = useState('info');
  
  // Loading state
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingText, setLoadingText] = useState('Processing...');
  
  // UI settings
  const [jumpToWaypointEnabled, setJumpToWaypointEnabled] = useState(false);
  const [currentRobotType, setCurrentRobotType] = useState('ur10e');
  
  // Modal state
  const [showRobotModal, setShowRobotModal] = useState(false);
  const [showFileListModal, setShowFileListModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showTransformModal, setShowTransformModal] = useState(false);
  const [showValidationPanel, setShowValidationPanel] = useState(false);

  const showStatus = useCallback((message, type = 'info') => {
    setStatusMessage(message);
    setStatusType(type);
  }, []);

  const startLoading = useCallback((text = 'Processing...') => {
    setIsLoading(true);
    setLoadingProgress(0);
    setLoadingText(text);
  }, []);

  const stopLoading = useCallback(() => {
    setIsLoading(false);
    setLoadingProgress(0);
  }, []);

  const value = {
    // Status
    statusMessage,
    statusType,
    showStatus,
    
    // Loading
    isLoading,
    setIsLoading,
    loadingProgress,
    setLoadingProgress,
    loadingText,
    setLoadingText,
    startLoading,
    stopLoading,
    
    // UI settings
    jumpToWaypointEnabled,
    setJumpToWaypointEnabled,
    currentRobotType,
    setCurrentRobotType,
    
    // Modals
    showRobotModal,
    setShowRobotModal,
    showFileListModal,
    setShowFileListModal,
    showUploadModal,
    setShowUploadModal,
    showTransformModal,
    setShowTransformModal,
    showValidationPanel,
    setShowValidationPanel,
  };

  return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
};
